// Recepción — mobile-first scan screen (/po/scan/:id). HID keyboard-wedge
// scanners type into the always-focused input + Enter; each scan resolves
// against the PO and accumulates per (session, device, part). Joined totals
// (across every device scanning this PO) refresh from reconcileReception
// after every scan so one handheld sees the others' contributions.
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, TextInput, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { getReception, reconcileReception, scanReception } from '../api/reception';
import AccessDenied from '../components/AccessDenied';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import TableSkeleton from '../components/TableSkeleton';
import { receptionStatusColor, receptionStatusLabel } from '../lib/format';
import { canUseReceiving } from '../lib/roles';
import { t } from '../lib/i18n';
import type { ReceptionSession, ReconReport, ScanResult, User } from '../api/types';

const DEVICE_KEY = 'pacebi_reception_device';

/** Read the persisted device id, or mint a short one from the username. */
function loadOrCreateDeviceId(username: string): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const generated = `${username || 'op'}-${Math.random().toString(36).slice(2, 6)}`;
    window.localStorage.setItem(DEVICE_KEY, generated);
    return generated;
  } catch {
    return username || 'op';
  }
}

function persistDeviceId(id: string) {
  try { window.localStorage.setItem(DEVICE_KEY, id); } catch { /* best-effort only */ }
}

export default function ReceptionScan() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-4 py-6 space-y-4 max-w-2xl mx-auto w-full">
        {canUseReceiving(user) ? <ReceptionScanInner user={user} /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}

type Flash =
  | { kind: 'unknown'; raw: string }
  | { kind: 'resolved'; part: string; pdesc: string; qty: number; onPo: boolean };

function ReceptionScanInner({ user }: { user: User }) {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);

  const [session, setSession] = useState<ReceptionSession | null>(null);
  const [report, setReport] = useState<ReconReport | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [device, setDevice] = useState(() => loadOrCreateDeviceId(user.username));
  const [buffer, setBuffer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [scanErr, setScanErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [keyedQty, setKeyedQty] = useState<Record<string, string>>({});

  const inputRef = useRef<HTMLInputElement>(null);

  const load = () => {
    if (!Number.isFinite(sessionId)) { setLoadErr(t.recepcion.errors.generic); return; }
    setLoadErr(null);
    Promise.all([getReception(sessionId), reconcileReception(sessionId)])
      .then(([s, r]) => { setSession(s); setReport(r); })
      .catch((e: Error) => setLoadErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sessionId]);

  // Keep the wedge target focused once the screen is ready to scan.
  useEffect(() => { inputRef.current?.focus(); }, [session]);

  // Transient flash — auto-clears so the screen doesn't get stuck showing a
  // stale "no reconocido" long after the operator moved on.
  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 4000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  function refreshReconcile() {
    return reconcileReception(sessionId).then(setReport);
  }

  function handleDeviceChange(v: string) {
    setDevice(v);
    persistDeviceId(v);
  }

  async function submitScan(code: string, qty?: number) {
    const trimmed = code.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setScanErr(null);
    try {
      const res: ScanResult = await scanReception(sessionId, {
        device: device.trim() || 'op',
        code: trimmed,
        qty,
      });
      if (!res.resolved) {
        setFlash({ kind: 'unknown', raw: res.raw ?? trimmed });
      } else {
        setFlash({
          kind: 'resolved',
          part: res.part ?? '',
          pdesc: res.pdesc ?? '',
          qty: res.qty ?? 0,
          onPo: res.on_po ?? true,
        });
      }
      await refreshReconcile();
    } catch (e) {
      setScanErr((e as Error).message ?? t.recepcion.scan.errors.generic);
    } finally {
      setSubmitting(false);
      inputRef.current?.focus();
    }
  }

  function handleBufferKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const code = buffer;
    setBuffer('');
    void submitScan(code);
  }

  function handleKeyedApply(part: string) {
    const raw = (keyedQty[part] ?? '').trim();
    if (raw === '') return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return;
    setKeyedQty((m) => ({ ...m, [part]: '' }));
    void submitScan(part, n);
  }

  if (loadErr) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorRetry message={loadErr} onRetry={load} />
      </div>
    );
  }
  if (session === null || report === null) {
    return (
      <div className="space-y-4">
        <BackLink />
        <TableSkeleton rows={6} cols={4} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackLink />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Title>OC #{session.onum}</Title>
            <Badge color={receptionStatusColor(session.status)}>{receptionStatusLabel(session.status)}</Badge>
          </div>
          <Text className="text-tremor-content text-sm">
            {session.sup_name || session.sup_id || '—'} · Tienda #{session.sid}
          </Text>
        </div>
        <Link to={`/po/scan/${session.id}/revision`} className="text-sm text-tremor-brand hover:underline">
          {t.recepcion.scan.reviewLink}
        </Link>
      </div>

      <Card className="space-y-3">
        <label className="text-sm block">
          <div className="text-tremor-content text-xs mb-1">{t.recepcion.scan.device}</div>
          <TextInput value={device} onValueChange={handleDeviceChange} className="max-w-xs" />
          <Text className="text-xs text-tremor-content mt-1">{t.recepcion.scan.deviceHint}</Text>
        </label>

        <div className="text-sm block">
          <div className="text-tremor-content text-xs mb-1">{t.recepcion.scan.input}</div>
          <input
            ref={inputRef}
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onKeyDown={handleBufferKeyDown}
            placeholder={t.recepcion.scan.inputPlaceholder}
            disabled={submitting}
            autoFocus
            className="w-full rounded-tremor-default border border-tremor-border px-4 py-4 text-xl font-semibold tabular-nums bg-tremor-background focus:outline-none focus:ring-2 focus:ring-tremor-brand"
            aria-label={t.recepcion.scan.input}
          />
          <Text className="text-xs text-tremor-content mt-1">{t.recepcion.scan.inputHint}</Text>
        </div>

        {scanErr && <Text className="text-red-600 dark:text-red-400 text-sm">{scanErr}</Text>}
      </Card>

      {flash && (
        flash.kind === 'unknown' ? (
          <Card className="border-2 border-red-500 bg-red-50 dark:bg-red-950/40">
            <Title className="text-red-700 dark:text-red-400">{t.recepcion.scan.noReconocido}</Title>
            <Text className="text-sm text-red-600 dark:text-red-400 mt-1">{flash.raw}</Text>
          </Card>
        ) : (
          <Card className={
            'border-2 ' + (flash.onPo
              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
              : 'border-amber-500 bg-amber-50 dark:bg-amber-950/40')
          }>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <Title className="text-base">{flash.part}</Title>
                <Text className="text-sm">{flash.pdesc}</Text>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold tabular-nums">{flash.qty}</div>
                <Text className="text-xs text-tremor-content">{t.recepcion.scan.deviceTotal}</Text>
              </div>
            </div>
            {!flash.onPo && (
              <Badge color="amber" className="mt-2">{t.recepcion.scan.unexpectedBadge}</Badge>
            )}
          </Card>
        )
      )}

      <Card className="p-0 overflow-hidden">
        <Title className="text-base px-4 pt-4">{t.recepcion.scan.linesTitle}</Title>
        {report.lines.length === 0 ? (
          <div className="p-4">
            <Text className="text-tremor-content text-sm">{t.recepcion.scan.empty}</Text>
          </div>
        ) : (
          <div className="overflow-x-auto mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.recepcion.scan.cols.part}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.recepcion.scan.cols.expected}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.recepcion.scan.cols.scanned}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{device}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.scan.keyedQty.label}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.lines.map((l) => (
                  <TableRow key={`${l.line}-${l.part}`}>
                    <TableCell>
                      <div className="font-medium">{l.part}</div>
                      <div className="text-xs text-tremor-content">{l.pdesc}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{l.expected}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{l.scanned}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.by_device[device] ?? 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          inputMode="numeric"
                          value={keyedQty[l.part] ?? ''}
                          onChange={(e) => setKeyedQty((m) => ({ ...m, [l.part]: e.target.value }))}
                          placeholder={t.recepcion.scan.keyedQty.placeholder}
                          className="w-20 rounded border border-tremor-border px-2 py-1 text-sm text-right tabular-nums bg-tremor-background"
                          aria-label={`${t.recepcion.scan.keyedQty.label} ${l.part}`}
                        />
                        <Button size="xs" variant="secondary" onClick={() => handleKeyedApply(l.part)}>
                          {t.recepcion.scan.keyedQty.apply}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {report.unexpected.length > 0 && (
        <Card className="p-0 overflow-hidden border-amber-300 dark:border-amber-700">
          <Title className="text-base px-4 pt-4">{t.recepcion.scan.unexpectedTitle}</Title>
          <div className="overflow-x-auto mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.recepcion.scan.cols.part}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.recepcion.scan.cols.scanned}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{device}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.unexpected.map((u) => (
                  <TableRow key={`${u.line}-${u.part}`}>
                    <TableCell className="font-medium">{u.part}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.scanned}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.by_device[device] ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/po/scan" className="text-sm text-tremor-brand hover:underline">
      {t.recepcion.back}
    </Link>
  );
}
